import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import {
    Plus,
    SearchLg,
    Edit01,
    Trash01,
    Mail01,
    Phone01,
    XCircle,
    User03,
    Building07,
    CheckVerified01,
} from '@untitledui/icons';
import { EmptyState } from '@/components/application/empty-state/empty-state';
import { Input } from '@/components/base/input/input';
import { TextArea } from '@/components/base/textarea/textarea';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { Button } from '@/components/base/buttons/button';
import { getContacts, createContact, updateContact, deleteContact, getAvailableRoles, validateContact, unvalidateContact } from '@/data/api/address-book';
import type { AddressBookEntry, AddressBookRole, CreateAddressBookEntryData } from '@/types/address-book';
import { useTranslation } from 'react-i18next';
import { useI18n } from '@/hooks/use-i18n';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useMe } from '@/hooks/api/use-auth';
import { useCompanySearch } from '@/hooks/api/use-company-search';
import { useCreateCompany } from '@/hooks/api/use-companies';
import { useCreateReceiverAssociation } from '@/hooks/api/use-receivers';
import { CompanyType } from '@/types/api/admin';
import { mapCompanyTypeToDisplay } from '@/types/company';

const CONTACTS_QUERY_KEY = ['contacts', 'address-book'] as const;

export const ShipperAddressBookPage = () => {
    const { t, i18n } = useTranslation(['pages:address-book', 'types', 'common']);
    const { translateRole } = useI18n();
    const toast = useToast();
    const queryClient = useQueryClient();
    const { data: currentUser } = useMe();
    const [searchParams, setSearchParams] = useSearchParams();

    // Get company filter from URL (from "See People" action in Business Partners)
    const companyFromUrl = searchParams.get('company') || '';
    const contactCompanyIdFromUrl = searchParams.get('contact_company_id') || '';

    // Fetch available roles from API (single source of truth)
    const { data: availableRoles = [] } = useQuery({
        queryKey: ['contacts', 'available-roles'],
        queryFn: getAvailableRoles,
        staleTime: 5 * 60 * 1000, // 5 minutes - roles don't change often
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState<AddressBookRole | ''>('');
    const [showModal, setShowModal] = useState(false);
    const [editingEntry, setEditingEntry] = useState<AddressBookEntry | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        notes: '',
        companyName: '',
        contactCompanyId: '' as string,
        role: 'Shipper' as AddressBookRole
    });

    // Company autocomplete state
    const [companySearchTerm, setCompanySearchTerm] = useState('');
    const [debouncedCompanySearch, setDebouncedCompanySearch] = useState('');
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [showQuickCreate, setShowQuickCreate] = useState(false);
    const [quickCreateType, setQuickCreateType] = useState<string>('receiver');

    // Debounce company search
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedCompanySearch(companySearchTerm), 400);
        return () => clearTimeout(timer);
    }, [companySearchTerm]);

    const { data: companySearchResults } = useCompanySearch(debouncedCompanySearch, showCompanyDropdown);
    const createCompanyMutation = useCreateCompany();
    const createAssociationMutation = useCreateReceiverAssociation();

    // Filter out current user's company from results
    const filteredCompanyResults = useMemo(() => {
        if (!companySearchResults) return [];
        return companySearchResults.filter((c) => c.company_id !== currentUser?.company_id);
    }, [companySearchResults, currentUser?.company_id]);

    // Initialize search term from URL company param
    useEffect(() => {
        if (companyFromUrl && !searchTerm) {
            setSearchTerm(companyFromUrl);
        }
    }, [companyFromUrl]);

    // Clear company filter from URL
    const handleClearCompanyFilter = () => {
        setSearchTerm('');
        setSearchParams((prev) => {
            prev.delete('company');
            prev.delete('contact_company_id');
            return prev;
        });
    };

    // Fetch contacts with filters
    const { data: entries = [], isLoading, isError } = useQuery({
        queryKey: [...CONTACTS_QUERY_KEY, { search: searchTerm, role: roleFilter, contactCompanyId: contactCompanyIdFromUrl }],
        queryFn: async () => {
            return await getContacts({
                search: searchTerm || undefined,
                role: roleFilter || undefined,
                contactCompanyId: contactCompanyIdFromUrl || undefined,
            });
        },
        staleTime: 30 * 1000, // 30 seconds
    });

    // Create mutation
    const createMutation = useMutation({
        mutationFn: async (data: CreateAddressBookEntryData) => {
            return await createContact(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
            setShowModal(false);
            toast.success(t('list.modal.createSuccess', { defaultValue: 'Contatto creato con successo' }));
        },
        onError: (error) => {
            console.error('Error creating contact:', error);
            toast.error(t('list.modal.createError', { defaultValue: 'Errore durante la creazione del contatto' }));
        },
    });

    // Update mutation
    const updateMutation = useMutation({
        mutationFn: async ({ id, data }: { id: string; data: CreateAddressBookEntryData }) => {
            return await updateContact(id, { ...data, id });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
            setShowModal(false);
            toast.success(t('list.modal.saveSuccess', { defaultValue: 'Contatto aggiornato con successo' }));
        },
        onError: (error) => {
            console.error('Error updating contact:', error);
            toast.error(t('list.modal.saveError', { defaultValue: 'Errore durante l\'aggiornamento del contatto' }));
        },
    });

    // Delete mutation
    const deleteMutation = useMutation({
        mutationFn: async (id: string) => {
            return await deleteContact(id);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
            setConfirmDeleteId(null);
            toast.success(t('list.confirmDelete.success', { defaultValue: 'Contatto eliminato con successo' }));
        },
        onError: (error) => {
            console.error('Error deleting contact:', error);
            toast.error(t('list.confirmDelete.error', { defaultValue: 'Errore durante l\'eliminazione del contatto' }));
        },
    });

    // Validate mutation
    const validateMutation = useMutation({
        mutationFn: async ({ id, validate }: { id: string; validate: boolean }) => {
            if (validate) {
                return await validateContact(id);
            } else {
                return await unvalidateContact(id);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
        },
    });

    // Get current language for role labels
    const currentLang = (i18n.language || 'en') as 'en' | 'it' | 'es';

    // Build role options from API data
    const roleOptions = [
        { id: '', label: t('list.allRoles') },
        ...availableRoles.map(role => ({
            id: role.id,
            label: role.label[currentLang] || role.label.en
        }))
    ];

    const roleFormOptions = availableRoles.map(role => ({
        id: role.id,
        label: role.label[currentLang] || role.label.en
    }));


    const getRoleColor = (role: string) => {
        // Assegna colori diversi ai ruoli per distinguerli visivamente
        const roleColors: Record<string, 'brand' | 'error' | 'warning' | 'success' | 'gray'> = {
            'Authority': 'error',
            'Freight Forwarder': 'brand',
            'Sales': 'warning',
            'Receiver': 'success',
            'Forwarder': 'brand',
            'Customs Broker': 'error',
            'Carrier': 'success',
            'Land Transport': 'success',
            'Port of Loading': 'brand',
            'Port of Discharge': 'brand',
            'Airport Staff': 'warning',
            'Ship Owner': 'brand',
        };
        return roleColors[role] || 'gray';
    };

    const handleAddEntry = () => {
        setEditingEntry(null);
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            notes: '',
            companyName: '',
            contactCompanyId: '',
            role: 'Shipper'
        });
        setCompanySearchTerm('');
        setShowQuickCreate(false);
        setShowModal(true);
    };

    const handleEditEntry = (entry: AddressBookEntry) => {
        setEditingEntry(entry);
        setFormData({
            firstName: entry.firstName,
            lastName: entry.lastName,
            email: entry.email,
            phone: entry.phone || '',
            notes: entry.notes || '',
            companyName: entry.companyName || '',
            contactCompanyId: entry.contactCompanyId || '',
            role: entry.role
        });
        setCompanySearchTerm('');
        setShowQuickCreate(false);
        setShowModal(true);
    };

    const handleSaveEntry = async () => {
        if (!formData.firstName || !formData.lastName || !formData.email) {
            return;
        }

        const contactData: CreateAddressBookEntryData = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone || undefined,
            notes: formData.notes || undefined,
            companyName: formData.companyName || undefined,
            contactCompanyId: formData.contactCompanyId || undefined,
            role: formData.role,
            accountId: currentUser?.company_id || '', // Not used by backend, but kept for compatibility
        };

        if (editingEntry) {
            updateMutation.mutate({ id: editingEntry.id, data: contactData });
        } else {
            createMutation.mutate(contactData);
        }
    };

    const handleDeleteEntry = (entryId: string) => {
        setConfirmDeleteId(entryId);
    };

    const handleConfirmDelete = () => {
        if (!confirmDeleteId) return;
        deleteMutation.mutate(confirmDeleteId);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('list.loading')}</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-error">{t('list.error', { defaultValue: 'Errore nel caricamento dei contatti' })}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-primary">{t('list.title')}</h1>
                    <p className="text-tertiary mt-1">
                        {t('list.subtitle')}
                    </p>
                </div>
                <Button
                    onClick={handleAddEntry}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                    iconLeading={<Plus className="size-5" />}
                >
                    {t('list.addContact')}
                </Button>
            </div>

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Input
                        placeholder={t('list.searchPlaceholder')}
                        value={searchTerm}
                        onChange={setSearchTerm}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('list.rolePlaceholder')}
                        items={roleOptions}
                        selectedKey={roleFilter}
                        onSelectionChange={(key) => setRoleFilter(key as AddressBookRole | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Address Book Table */}
            <TableCard.Root>
                <TableCard.Header
                    title={t('list.title') + ` (${entries.length})`}
                    description={t('list.description')}
                />

                <Table>
                    <Table.Header>
                        <Table.Head label={t('list.headers.firstName')} isRowHeader />
                        <Table.Head label={t('list.headers.lastName')} />
                        <Table.Head label={t('list.headers.company', { defaultValue: 'Company' })} />
                        <Table.Head label={t('list.headers.email')} />
                        <Table.Head label={t('list.headers.phone', { defaultValue: 'Phone' })} />
                        <Table.Head label={t('list.headers.role')} />
                        <Table.Head label={t('list.headers.actions')} />
                    </Table.Header>
                    <Table.Body>
                        {entries.map((entry) => {
                            return (
                                <Table.Row key={entry.id}>
                                    <Table.Cell>
                                        <div className="flex items-center gap-1.5">
                                            <span className="font-medium text-primary">{entry.firstName}</span>
                                            {entry.isVerified && (
                                                <CheckVerified01 className="size-3.5 text-success-600" />
                                            )}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="font-medium text-primary">{entry.lastName}</div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {entry.companyName ? (
                                            <div className="flex items-center gap-2">
                                                <Building07 className="size-4 text-tertiary" />
                                                <span className="text-sm text-tertiary">{entry.companyName}</span>
                                            </div>
                                        ) : (
                                            <span className="text-sm text-quaternary">-</span>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="flex items-center gap-2">
                                            <Mail01 className="size-4 text-tertiary" />
                                            <span className="text-sm text-tertiary">{entry.email}</span>
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell>
                                        {entry.phone ? (
                                            <div className="flex items-center gap-2">
                                                <Phone01 className="size-4 text-tertiary" />
                                                <span className="text-sm text-tertiary">{entry.phone}</span>
                                            </div>
                                        ) : (
                                            <span className="text-sm text-quaternary">-</span>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Badge color={getRoleColor(entry.role)} size="sm">
                                            {translateRole(entry.role)}
                                        </Badge>
                                    </Table.Cell>
                                    <Table.Cell>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => validateMutation.mutate({ id: entry.id, validate: !entry.isVerified })}
                                                className={entry.isVerified ? "text-success-600 hover:text-success-700" : "text-tertiary hover:text-success-600"}
                                                title={entry.isVerified ? "Remove validation" : "Validate contact"}
                                            >
                                                <CheckVerified01 className="size-4" />
                                            </button>
                                            <button
                                                onClick={() => handleEditEntry(entry)}
                                                className="text-brand-secondary hover:text-brand-secondary_hover"
                                            >
                                                <Edit01 className="size-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteEntry(entry.id)}
                                                className="text-error-600 hover:text-error-700"
                                            >
                                                <Trash01 className="size-4" />
                                            </button>
                                        </div>
                                    </Table.Cell>
                                </Table.Row>
                            );
                        })}
                    </Table.Body>
                </Table>
            </TableCard.Root>

            {entries.length === 0 && (
                <EmptyState size="md" className="py-12">
                    <EmptyState.Header>
                        <EmptyState.FeaturedIcon icon={User03} theme="modern" color="gray" />
                    </EmptyState.Header>
                    <EmptyState.Content>
                        <EmptyState.Title>
                            {companyFromUrl
                                ? t('list.noContacts.companyTitle', { defaultValue: 'No contacts found' })
                                : searchTerm || roleFilter
                                    ? t('list.noContacts.title')
                                    : t('list.noContacts.title')
                            }
                        </EmptyState.Title>
                        <EmptyState.Description>
                            {companyFromUrl
                                ? t('list.noContacts.companyMessage', { company: companyFromUrl, defaultValue: `Currently there are no people associated to ${companyFromUrl}` })
                                : searchTerm || roleFilter
                                    ? t('list.noContacts.message')
                                    : t('list.noContacts.emptyMessage')
                            }
                        </EmptyState.Description>
                    </EmptyState.Content>
                    {companyFromUrl ? (
                        <EmptyState.Footer>
                            <Button
                                onClick={handleClearCompanyFilter}
                                color="secondary"
                                size="md"
                            >
                                {t('list.noContacts.clearFilter', { defaultValue: 'Clear Filter' })}
                            </Button>
                        </EmptyState.Footer>
                    ) : !searchTerm && !roleFilter ? (
                        <EmptyState.Footer>
                            <Button
                                onClick={handleAddEntry}
                                color="primary"
                                size="md"
                                iconLeading={<Plus className="size-5" />}
                            >
                                {t('list.noContacts.addFirst')}
                            </Button>
                        </EmptyState.Footer>
                    ) : null}
                </EmptyState>
            )}

            {/* Add/Edit Modal */}
            {showModal && (
                <ModalOverlay isOpen={showModal} onOpenChange={setShowModal}>
                    <Modal>
                        <Dialog>
                            <div className="w-full max-w-2xl rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">
                                        {editingEntry ? t('list.modal.editTitle') : t('list.modal.addTitle')}
                                    </h2>
                                    <button
                                        onClick={() => setShowModal(false)}
                                        className="text-tertiary hover:text-primary"
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <div className="space-y-6">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium text-primary mb-2 block">
                                                {t('list.modal.fields.firstName')}
                                            </label>
                                            <Input
                                                placeholder={t('list.modal.fields.firstNamePlaceholder')}
                                                value={formData.firstName}
                                                onChange={(value) => setFormData({ ...formData, firstName: value })}
                                                size="md"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-sm font-medium text-primary mb-2 block">
                                                {t('list.modal.fields.lastName')}
                                            </label>
                                            <Input
                                                placeholder={t('list.modal.fields.lastNamePlaceholder')}
                                                value={formData.lastName}
                                                onChange={(value) => setFormData({ ...formData, lastName: value })}
                                                size="md"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-primary mb-2 block">
                                            {t('list.modal.fields.company', { defaultValue: 'Company' })}
                                        </label>
                                        {formData.contactCompanyId ? (
                                            /* Selected company — show as chip with clear button */
                                            <div className="flex items-center gap-2">
                                                <span className="flex-1 px-3 py-2 bg-secondary rounded-lg text-sm text-primary">
                                                    {formData.companyName}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setFormData({ ...formData, companyName: '', contactCompanyId: '' });
                                                        setCompanySearchTerm('');
                                                    }}
                                                    className="text-tertiary hover:text-primary"
                                                >
                                                    <XCircle className="size-5" />
                                                </button>
                                            </div>
                                        ) : showQuickCreate ? (
                                            /* Quick-create inline form */
                                            <div className="space-y-3 p-3 rounded-lg border border-secondary bg-secondary/50">
                                                <div className="text-sm font-medium text-primary">
                                                    Create new company: &quot;{companySearchTerm.trim()}&quot;
                                                </div>
                                                <Select
                                                    placeholder="Company type"
                                                    items={[
                                                        { id: 'receiver', label: 'Receiver' },
                                                        { id: 'carrier', label: 'Carrier' },
                                                        { id: 'freight_forwarder', label: 'Freight Forwarder' },
                                                        { id: 'customs_broker', label: 'Customs Broker' },
                                                    ]}
                                                    selectedKey={quickCreateType}
                                                    onSelectionChange={(key) => setQuickCreateType(key as string)}
                                                    size="md"
                                                >
                                                    {(item) => (
                                                        <Select.Item key={item.id} id={item.id}>
                                                            {item.label}
                                                        </Select.Item>
                                                    )}
                                                </Select>
                                                <div className="flex gap-2">
                                                    <Button
                                                        type="button"
                                                        color="secondary"
                                                        size="sm"
                                                        onClick={() => setShowQuickCreate(false)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        color="primary"
                                                        size="sm"
                                                        isLoading={createCompanyMutation.isPending}
                                                        onClick={async () => {
                                                            try {
                                                                const newCompany = await createCompanyMutation.mutateAsync({
                                                                    company_name: companySearchTerm.trim(),
                                                                    company_type: quickCreateType as CompanyType,
                                                                    is_active: true,
                                                                });
                                                                // Create association with current user's company
                                                                if (currentUser?.company_id) {
                                                                    try {
                                                                        await createAssociationMutation.mutateAsync({
                                                                            shipper_company_id: currentUser.company_id,
                                                                            receiver_company_id: newCompany.company_id,
                                                                        });
                                                                    } catch { /* association may already exist */ }
                                                                }
                                                                setFormData({
                                                                    ...formData,
                                                                    companyName: newCompany.company_name,
                                                                    contactCompanyId: newCompany.company_id,
                                                                });
                                                                setShowQuickCreate(false);
                                                                setCompanySearchTerm('');
                                                                toast.success('Company created');
                                                            } catch (err: any) {
                                                                toast.error(err.message || 'Failed to create company');
                                                            }
                                                        }}
                                                    >
                                                        Create
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            /* Company autocomplete search */
                                            <div className="relative">
                                                <Input
                                                    placeholder={t('list.modal.fields.companyPlaceholder', { defaultValue: 'Search or type company name' })}
                                                    value={companySearchTerm || formData.companyName}
                                                    onChange={(value) => {
                                                        setCompanySearchTerm(value);
                                                        setFormData({ ...formData, companyName: value, contactCompanyId: '' });
                                                        setShowCompanyDropdown(true);
                                                    }}
                                                    onFocus={() => setShowCompanyDropdown(true)}
                                                    onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 200)}
                                                    icon={SearchLg}
                                                    size="md"
                                                />
                                                {showCompanyDropdown && debouncedCompanySearch.length >= 2 && (
                                                    <div className="absolute z-10 w-full mt-1 max-h-48 overflow-auto rounded-lg border border-secondary bg-primary shadow-lg">
                                                        {filteredCompanyResults.map((company) => (
                                                            <button
                                                                key={company.company_id}
                                                                type="button"
                                                                className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2"
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                onClick={() => {
                                                                    setFormData({
                                                                        ...formData,
                                                                        companyName: company.company_name,
                                                                        contactCompanyId: company.company_id,
                                                                    });
                                                                    setCompanySearchTerm('');
                                                                    setShowCompanyDropdown(false);
                                                                }}
                                                            >
                                                                <span className="flex-1 text-primary font-medium">{company.company_name}</span>
                                                                <Badge
                                                                    color={company.company_type === 'carrier' ? 'warning' : company.company_type === 'receiver' ? 'brand' : 'gray'}
                                                                    size="sm"
                                                                >
                                                                    {mapCompanyTypeToDisplay(company.company_type as any)}
                                                                </Badge>
                                                            </button>
                                                        ))}
                                                        {/* Quick-create option */}
                                                        <button
                                                            type="button"
                                                            className="w-full px-3 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2 border-t border-secondary"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => {
                                                                setShowCompanyDropdown(false);
                                                                setShowQuickCreate(true);
                                                            }}
                                                        >
                                                            <Plus className="size-4 text-brand-600" />
                                                            <span className="text-brand-600 font-medium">
                                                                Create new: &quot;{companySearchTerm.trim()}&quot;
                                                            </span>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium text-primary mb-2 block">
                                                {t('list.modal.fields.email')}
                                            </label>
                                            <Input
                                                placeholder={t('list.modal.fields.emailPlaceholder')}
                                                value={formData.email}
                                                onChange={(value) => setFormData({ ...formData, email: value })}
                                                size="md"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-sm font-medium text-primary mb-2 block">
                                                {t('list.modal.fields.phone', { defaultValue: 'Phone' })}
                                            </label>
                                            <Input
                                                placeholder={t('list.modal.fields.phonePlaceholder', { defaultValue: 'Enter phone number' })}
                                                value={formData.phone}
                                                onChange={(value) => setFormData({ ...formData, phone: value })}
                                                size="md"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-primary mb-2 block">
                                            {t('list.modal.fields.role')}
                                        </label>
                                        <Select
                                            placeholder={t('list.modal.fields.role', { ns: 'common' })}
                                            items={roleFormOptions}
                                            selectedKey={formData.role}
                                            onSelectionChange={(key) => setFormData({ ...formData, role: key as AddressBookRole })}
                                            size="md"
                                        >
                                            {(item) => (
                                                <Select.Item key={item.id} id={item.id}>
                                                    {item.label}
                                                </Select.Item>
                                            )}
                                        </Select>
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium text-primary mb-2 block">
                                            {t('list.modal.fields.notes', { defaultValue: 'Notes' })}
                                        </label>
                                        <TextArea
                                            placeholder={t('list.modal.fields.notesPlaceholder', { defaultValue: 'Add any additional notes about this contact...' })}
                                            value={formData.notes}
                                            onChange={(value) => setFormData({ ...formData, notes: value })}
                                            rows={3}
                                        />
                                    </div>

                                    <div className="flex justify-end gap-3">
                                        <Button
                                            onClick={() => setShowModal(false)}
                                            color="secondary"
                                            size="md"
                                        >
                                            {t('list.modal.cancel')}
                                        </Button>
                                        <Button
                                            onClick={handleSaveEntry}
                                            disabled={!formData.firstName || !formData.lastName || !formData.email || createMutation.isPending || updateMutation.isPending}
                                            color="primary"
                                            size="md"
                                        >
                                            {createMutation.isPending || updateMutation.isPending
                                                ? t('common.loading', { defaultValue: 'Caricamento...' })
                                                : editingEntry
                                                    ? t('list.modal.save')
                                                    : t('list.modal.create')
                                            }
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Confirm Delete Modal */}
            {confirmDeleteId && (
                <ModalOverlay isOpen={!!confirmDeleteId} onOpenChange={(open) => !open && setConfirmDeleteId(null)}>
                    <Modal>
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-semibold text-primary">{t('list.confirmDelete.title')}</h2>
                                    <button
                                        onClick={() => setConfirmDeleteId(null)}
                                        className="text-tertiary hover:text-primary"
                                    >
                                        <XCircle className="size-5" />
                                    </button>
                                </div>
                                <p className="text-tertiary mb-6">{t('list.confirmDelete.message')}</p>
                                <div className="flex justify-end gap-3">
                                    <Button onClick={() => setConfirmDeleteId(null)} color="secondary" size="md">
                                        {t('list.confirmDelete.cancel')}
                                    </Button>
                                    <Button
                                        onClick={handleConfirmDelete}
                                        color="primary-destructive"
                                        size="md"
                                        disabled={deleteMutation.isPending}
                                    >
                                        {deleteMutation.isPending
                                            ? t('common.loading', { defaultValue: 'Caricamento...' })
                                            : t('list.confirmDelete.delete')
                                        }
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
};

